import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  LineReceiveInput,
  effectiveLineItemId,
  remainingReceiveQty,
  isLineReceivable,
} from '../../../store/stockRequestStore';
import { getRequestTheme } from './theme';

export type ReceiveTarget = {
  request: StockRequest;
  line: StockRequestLine;
};

interface LineDraft {
  qty: number;
  units: { serial: string; mac: string }[];
}

interface Props {
  targets: ReceiveTarget[];
  isDark: boolean;
  showLineNames?: boolean;
  onCancel?: () => void;
  onComplete: (message: string) => void;
}

function emptyUnits(n: number): { serial: string; mac: string }[] {
  return Array.from({ length: Math.max(0, n) }, () => ({ serial: '', mac: '' }));
}

const ReceiveStockPanel: React.FC<Props> = ({
  targets,
  isDark,
  showLineNames = false,
  onCancel,
  onComplete,
}) => {
  const theme = getRequestTheme(isDark);
  const { items } = useInventoryStore();
  const { receiveLines } = useStockRequestStore();
  const receivable = useMemo(
    () => targets.filter(t => isLineReceivable(t.line)),
    [targets],
  );

  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [skipSerial, setSkipSerial] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const receivableKey = receivable.map(t => `${t.line.id}:${remainingReceiveQty(t.line)}`).join('|');

  useEffect(() => {
    setDrafts(prev => {
      const next: Record<string, LineDraft> = {};
      for (const { line } of receivable) {
        const qty = remainingReceiveQty(line);
        const existing = prev[line.id];
        if (existing && existing.qty <= qty) {
          next[line.id] = {
            qty: existing.qty,
            units: Array.from({ length: existing.qty }, (_, i) => existing.units[i] ?? { serial: '', mac: '' }),
          };
        } else {
          next[line.id] = { qty, units: emptyUnits(qty) };
        }
      }
      return next;
    });
  }, [receivableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemById = useMemo(() => {
    const map = new Map(items.map(i => [i.id, i]));
    return map;
  }, [items]);

  const serialProgress = useMemo(() => {
    let required = 0;
    let filled = 0;
    if (skipSerial) return { required, filled };
    for (const { line } of receivable) {
      const draft = drafts[line.id];
      if (!draft) continue;
      const item = itemById.get(effectiveLineItemId(line) ?? '');
      if (!item) continue;
      for (let i = 0; i < draft.qty; i++) {
        if (item.requiresSerial) {
          required++;
          if (draft.units[i]?.serial?.trim()) filled++;
        }
        if (item.requiresMac) {
          required++;
          if (draft.units[i]?.mac?.trim()) filled++;
        }
      }
    }
    return { required, filled };
  }, [receivable, drafts, itemById, skipSerial]);

  const canConfirm = skipSerial
    ? skipReason.trim().length > 0
    : serialProgress.required === 0 || serialProgress.filled >= serialProgress.required;

  const updateQty = (line: StockRequestLine, qty: number) => {
    const max = remainingReceiveQty(line);
    const n = Math.min(Math.max(1, qty), max);
    setDrafts(prev => {
      const current = prev[line.id];
      if (!current) return prev;
      return {
        ...prev,
        [line.id]: {
          qty: n,
          units: Array.from({ length: n }, (_, i) => current.units[i] ?? { serial: '', mac: '' }),
        },
      };
    });
  };

  const updateUnit = (lineId: string, idx: number, field: 'serial' | 'mac', value: string) => {
    setDrafts(prev => {
      const current = prev[lineId];
      if (!current) return prev;
      const units = [...current.units];
      units[idx] = { ...units[idx], [field]: value };
      return { ...prev, [lineId]: { ...current, units } };
    });
  };

  const handleConfirm = async () => {
    if (!canConfirm || receivable.length === 0) return;
    setSaving(true);
    setError('');

    const byRequest = new Map<string, LineReceiveInput[]>();
    for (const { request, line } of receivable) {
      const draft = drafts[line.id];
      if (!draft || draft.qty <= 0) continue;
      const list = byRequest.get(request.id) ?? [];
      list.push({
        lineId: line.id,
        qty: draft.qty,
        units: draft.units.map(u => ({ serial: u.serial, mac: u.mac })),
        skipSerialValidation: skipSerial,
        skipReason: skipSerial ? skipReason.trim() : undefined,
      });
      byRequest.set(request.id, list);
    }

    let lastMessage = 'Stock received and added to inventory.';
    for (const [requestId, receipts] of Array.from(byRequest.entries())) {
      const result = await receiveLines(requestId, receipts);
      if (!result.success) {
        setSaving(false);
        setError(result.message);
        return;
      }
      lastMessage = result.message;
    }

    setSaving(false);
    onComplete(lastMessage);
  };

  const inputCls =
    'text-sm px-2 py-1.5 rounded border w-full focus:outline-none focus:ring-1 focus:ring-blue-500 box-border';
  const qtyInputCls =
    'text-sm text-center w-16 h-7 px-1 py-0 rounded border box-border leading-none focus:outline-none focus:ring-1 focus:ring-blue-500';

  if (receivable.length === 0) {
    return (
      <p className="text-sm" style={{ color: theme.muted }}>
        Nothing to receive. Mark as Ordered first, and assign a catalogue item.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {receivable.map(({ line }) => {
        const draft = drafts[line.id];
        const remaining = remainingReceiveQty(line);
        const item = itemById.get(effectiveLineItemId(line) ?? '');
        const needsSerial = !!item?.requiresSerial;
        const needsMac = !!item?.requiresMac;
        const lineName = item?.name
          ?? line.freeTextDescription
          ?? 'Item';
        return (
          <div key={line.id} className="space-y-2">
            {showLineNames && (
              <p className="text-sm font-medium truncate" style={{ color: theme.text }} title={lineName}>
                {lineName}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm" style={{ color: theme.muted }}>
                Qty now
                <span className="ml-1.5" style={{ color: theme.text }}>
                  of {remaining} remaining
                </span>
              </span>
              <input
                type="number"
                min={1}
                max={remaining}
                value={draft?.qty ?? remaining}
                onChange={e => updateQty(line, parseInt(e.target.value, 10) || 1)}
                className={qtyInputCls}
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                  colorScheme: isDark ? 'dark' : 'light',
                }}
              />
            </div>
            {(needsSerial || needsMac) && !skipSerial && draft && (
              <div className="space-y-1.5">
                <p className="text-[11px]" style={{ color: theme.muted }}>
                  {needsSerial && needsMac ? 'Serial & MAC required' : needsSerial ? 'Serial required' : 'MAC required'}
                  {serialProgress.required > 0 && showLineNames === false && (
                    <> · {serialProgress.filled}/{serialProgress.required} complete</>
                  )}
                </p>
                {Array.from({ length: draft.qty }, (_, i) => (
                  <div key={`${line.id}-unit-${i}`} className="flex items-center gap-1.5">
                    {draft.qty > 1 && (
                      <span className="text-[11px] w-4 flex-shrink-0" style={{ color: theme.muted }}>{i + 1}</span>
                    )}
                    {needsSerial && (
                      <input
                        type="text"
                        placeholder="Serial"
                        value={draft.units[i]?.serial ?? ''}
                        onChange={e => updateUnit(line.id, i, 'serial', e.target.value)}
                        className={inputCls}
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                      />
                    )}
                    {needsMac && (
                      <input
                        type="text"
                        placeholder="MAC"
                        value={draft.units[i]?.mac ?? ''}
                        onChange={e => updateUnit(line.id, i, 'mac', e.target.value)}
                        className={inputCls}
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {serialProgress.required > 0 && (
        <div className="space-y-2 pt-1">
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: theme.muted }}>
            <input
              type="checkbox"
              checked={skipSerial}
              onChange={e => setSkipSerial(e.target.checked)}
            />
            Receive without serials/MACs (requires reason)
          </label>
          {skipSerial && (
            <input
              type="text"
              placeholder="Reason…"
              value={skipReason}
              onChange={e => setSkipReason(e.target.value)}
              className={inputCls}
              style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
            />
          )}
          {!canConfirm && !skipSerial && (
            <div className="flex items-start gap-2 text-xs text-amber-500">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>Enter all required serial numbers and MAC addresses before adding to stock.</span>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center h-8 px-3 rounded text-xs font-medium"
            style={{
              border: `1px solid ${theme.border}`,
              color: theme.text,
              backgroundColor: isDark ? '#1c2129' : '#f3f4f6',
            }}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          disabled={saving || !canConfirm}
          onClick={() => void handleConfirm()}
          className="inline-flex items-center h-8 px-3 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Confirm & add to stock'}
        </button>
      </div>
    </div>
  );
};

export default ReceiveStockPanel;
